import BaseApiService from './BaseApiService'
import type { CurrentBill } from '../types/billingStatement'

/** POST /v1/team/status — the caller's team and whether it is closed. */
export interface TeamStatus {
  teamId: string
  name: string
  closed: boolean
  closedAt: string | null
  /** For a closed team: the end of the reopen window; its data is erased after it. */
  eraseAfter: string | null
  canReopen: boolean
}

/** POST /v1/team/close/preview — what closing would do, for the confirmation modal. */
export interface ClosePreview {
  teamName: string
  /** A template is on legal hold: closing is refused. */
  legalHold: boolean
  mfaRequired: boolean
  mfaMethod: 'totp' | 'email' | null
  eraseAfter: string
  graceDays: number
  /** The current bill: the closure charges it in full now. */
  finalBill: CurrentBill
}

export interface CloseResult {
  closed: boolean
  closedAt: string
  eraseAfter: string
  queuedRendersCancelled: number
  finalBill: { result: string; totalCents: number; currency: string } | null
}

export interface PendingInvite {
  teamId: string
  teamName: string
  role: number
  expiresAt: string
}

class TeamApiService extends BaseApiService {
  status(): Promise<TeamStatus> {
    return this.post('/v1/team/status', {})
  }

  /** 409 LAST_ADMIN for the only Team Admin; 409 DUAL_CONTROL_FALLBACK_REQUIRED when a fallback must be chosen. */
  leave(dualControlFallback?: 'auto' | 'self'): Promise<{ left: boolean }> {
    return this.post('/v1/team/leave', { dualControlFallback })
  }

  closePreview(): Promise<ClosePreview> {
    return this.post('/v1/team/close/preview', {})
  }

  /** For an admin whose MFA method is email: sends the one-time code the close needs. */
  sendCloseCode(): Promise<{ sent: boolean }> {
    return this.post('/v1/team/close/send-code', {})
  }

  close(teamName: string, mfaCode?: string): Promise<CloseResult> {
    return this.post('/v1/team/close', { teamName, mfaCode: mfaCode || undefined })
  }

  reopen(): Promise<{ reopened: boolean }> {
    return this.post('/v1/team/reopen', {})
  }

  /** An account on no team creates one and becomes its Team Admin. */
  create(name: string): Promise<{ teamId: string }> {
    return this.post('/v1/team/create', { name })
  }

  pendingInvite(): Promise<{ invite: PendingInvite | null }> {
    return this.post('/v1/team/invites/pending', {})
  }

  acceptInvite(): Promise<{ teamId: string }> {
    return this.post('/v1/team/invites/accept', {})
  }

  declineInvite(): Promise<{ declined: boolean }> {
    return this.post('/v1/team/invites/decline', {})
  }
}

export const teamApiService = new TeamApiService()
